/**
 * NHL Game Helper Functions
 * Utility functions for formatting and processing NHL game data from API
 */

import { formatGameStatus, getGameState } from '../rtdb/rtdbConfig';

/**
 * Format game time for display
 * @param {string} dateString - ISO date string from API
 * @param {string} time - Time string from API
 * @returns {string} Formatted time
 */
export const formatGameTime = (dateString, time) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  } catch (error) {
    return time || '';
  }
};

/**
 * Format date only
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatGameDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleString('en-US', options);
  } catch (error) {
    return '';
  }
};

/**
 * Parse period scores from API format ("home-away")
 * @param {Object} periods - Periods object from API
 * @returns {Object} Parsed period scores
 */
export const parsePeriodScores = (periods) => {
  if (!periods) return {};

  const parsePeriod = (periodString) => {
    if (!periodString) return null;
    const [home, away] = periodString.split('-').map(Number);
    return { home, away };
  };

  return {
    first: parsePeriod(periods.first),
    second: parsePeriod(periods.second),
    third: parsePeriod(periods.third),
    overtime: parsePeriod(periods.overtime),
    penalties: parsePeriod(periods.penalties)
  };
};

/**
 * Get status badge color class
 * @param {string} status - Game status code
 * @returns {string} Tailwind color classes
 */
export const getStatusBadgeColor = (status) => {
  const state = getGameState(status);

  switch (state) {
    case 'LIVE':
      return 'bg-red-600 text-white animate-pulse';
    case 'FINAL':
      return 'bg-gray-600 text-white';
    case 'SCHEDULED':
      return 'bg-blue-600 text-white';
    default:
      return 'bg-yellow-600 text-white';
  }
};

/**
 * Check if game is today
 * @param {string} dateString - Game date ISO string
 * @returns {boolean}
 */
export const isGameToday = (dateString) => {
  if (!dateString) return false;

  const gameDate = new Date(dateString).toISOString().split('T')[0];
  const today = new Date().toISOString().split('T')[0];
  return gameDate === today;
};

/**
 * Check if game is in the past
 * @param {string} dateString - Game date ISO string
 * @returns {boolean}
 */
export const isGamePast = (dateString) => {
  if (!dateString) return false;

  const gameDate = new Date(dateString);
  const now = new Date();
  return gameDate < now;
};

/**
 * Get team record display
 * @param {Object} team - Team object (if it has record data)
 * @returns {string} Record string like "10-5-2"
 */
export const formatTeamRecord = (team) => {
  // Placeholder - team record might not be in basic team object
  // May need to fetch from standings or team stats
  return team?.record || '';
};

/**
 * Format score display with winner highlighting
 * @param {number} homeScore - Home team score
 * @param {number} awayScore - Away team score
 * @param {boolean} isHome - Whether to check if home team won
 * @returns {Object} { score, isWinner }
 */
export const getScoreDisplay = (homeScore, awayScore, isHome) => {
  const score = isHome ? homeScore : awayScore;
  const isWinner = isHome
    ? homeScore > awayScore
    : awayScore > homeScore;

  return { score, isWinner };
};

/**
 * Build period-by-period summary
 * @param {Object} periods - Periods object from API
 * @returns {Array} Array of period summaries
 */
export const buildPeriodSummary = (periods) => {
  const parsed = parsePeriodScores(periods);
  const summary = [];

  if (parsed.first) {
    summary.push({ period: '1st', ...parsed.first });
  }
  if (parsed.second) {
    summary.push({ period: '2nd', ...parsed.second });
  }
  if (parsed.third) {
    summary.push({ period: '3rd', ...parsed.third });
  }
  if (parsed.overtime) {
    summary.push({ period: 'OT', ...parsed.overtime });
  }
  if (parsed.penalties) {
    summary.push({ period: 'SO', ...parsed.penalties });
  }

  return summary;
};

/**
 * Get live indicator text
 * @param {string} status - Game status code
 * @param {string} timer - Game timer (if available)
 * @returns {string} Live indicator text
 */
export const getLiveIndicator = (status, timer) => {
  const statusText = formatGameStatus(status);

  if (timer) {
    return `${statusText} - ${timer}`;
  }

  return statusText;
};

export default {
  formatGameTime,
  formatGameDate,
  parsePeriodScores,
  getStatusBadgeColor,
  isGameToday,
  isGamePast,
  formatTeamRecord,
  getScoreDisplay,
  buildPeriodSummary,
  getLiveIndicator
};
